import { useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { useMountEffect } from "@/lib/useMountEffect";
import { ShieldAlert, ShieldCheck, ListFilter, RefreshCw } from "lucide-react";

const SCORE_BG = (s) => {
  if (s >= 70) return "bg-destructive text-white";
  if (s >= 40) return "bg-amber-200 text-amber-900";
  return "bg-muted text-muted-foreground";
};

export default function AdminSecurity() {
  const [summary, setSummary] = useState(null);
  const [spam, setSpam] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formFilter, setFormFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (formFilter) params.form = formFilter;
      if (actionFilter) params.action = actionFilter;
      const [s, sl, al] = await Promise.all([
        api.get("/security/summary"),
        api.get("/security/spam-log", { params }),
        api.get("/security/audit-log"),
      ]);
      setSummary(s.data);
      setSpam(sl.data.items);
      setAudit(al.data.items);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useMountEffect(reload);

  return (
    <AdminLayout
      title="Security"
      subtitle="Spam attempts, audit log and platform health"
      actions={
        <button onClick={reload} className="btn-secondary inline-flex items-center gap-2" data-testid="security-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      }
    >
      {error && <div className="mb-4 text-sm text-destructive border border-destructive/30 px-3 py-2 bg-destructive/5">{error}</div>}

      {/* Summary tiles */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="security-summary">
        {summary && Object.entries({
          "Spam attempts (7d)": summary.spam_attempts,
          "Rejected (7d)": summary.rejected,
          "Flagged (7d)": summary.flagged,
          "Failed logins (7d)": summary.failed_logins,
        }).map(([label, value]) => (
          <div key={label} className="border border-border bg-white px-4 py-3" data-testid={`summary-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
            <div className="text-2xl font-heading font-semibold">{value ?? 0}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
          </div>
        ))}
      </section>

      {/* Spam log */}
      <section className="mb-10" data-testid="spam-log-section">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <h2 className="overline inline-flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-destructive" /> Spam attempts
          </h2>
          <div className="flex items-center gap-2">
            <select className="input-swiss text-xs h-8 py-0" value={formFilter} onChange={(e) => setFormFilter(e.target.value)} data-testid="spam-form-filter">
              <option value="">All forms</option>
              <option value="candidates">Candidates</option>
              <option value="enquiries">Enquiries</option>
              <option value="contacts">Contacts</option>
            </select>
            <select className="input-swiss text-xs h-8 py-0" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} data-testid="spam-action-filter">
              <option value="">All actions</option>
              <option value="reject">Reject</option>
              <option value="flag">Flag</option>
              <option value="accept">Accept</option>
            </select>
            <button onClick={reload} className="btn-secondary inline-flex items-center gap-1.5 text-xs h-8 py-0 px-3" data-testid="spam-apply">
              <ListFilter className="h-3 w-3" /> Apply
            </button>
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : spam.length === 0 ? (
          <div className="border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="spam-log-empty">
            No spam attempts logged.
          </div>
        ) : (
          <div className="border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">When</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Form</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Score</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Action</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Reasons</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">IP</th>
                </tr>
              </thead>
              <tbody>
                {spam.map((s) => (
                  <tr key={s.id} className="border-t border-border" data-testid={`spam-row-${s.id}`}>
                    <td className="px-4 py-2 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-xs">{s.form}</td>
                    <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[11px] font-mono ${SCORE_BG(s.score)}`}>{s.score}</span></td>
                    <td className="px-4 py-2 text-xs uppercase">{s.action}</td>
                    <td className="px-4 py-2 text-xs">{(s.reasons || []).join(", ")}</td>
                    <td className="px-4 py-2 text-[11px] font-mono text-muted-foreground">{s.signals?.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Audit log */}
      <section data-testid="audit-log-section">
        <h2 className="overline inline-flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4 text-execution" /> Audit log
        </h2>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : audit.length === 0 ? (
          <div className="border border-dashed border-border py-10 text-center text-sm text-muted-foreground" data-testid="audit-log-empty">
            No audit events yet.
          </div>
        ) : (
          <div className="border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">When</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Action</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Target</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Actor</th>
                  <th className="text-left px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.slice(0, 100).map((a) => (
                  <tr key={a.id} className="border-t border-border" data-testid={`audit-row-${a.id}`}>
                    <td className="px-4 py-2 text-xs">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-xs font-mono">{a.action}</td>
                    <td className="px-4 py-2 text-xs">{a.target || "—"}</td>
                    <td className="px-4 py-2 text-[11px] font-mono text-muted-foreground">{a.actor_id ? a.actor_id.slice(0, 8) + "…" : "—"}</td>
                    <td className="px-4 py-2 text-[11px] font-mono text-muted-foreground">{a.meta?.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}
